/**
 * Column definition recorded into `columnsJson` and rendered by the player.
 * Keys must match the slim row object keys — typically the Kendo `field`.
 */
export interface IRynReplayGridColumnDef {
  key: string;
  label: string;
  align?: 'left' | 'right';
  format?: 'text' | 'number' | 'boolean';
}

/**
 * Minimal column surface we read off Kendo (avoids a hard dependency on the
 * GridComponent type in shared capture helpers).
 */
export interface IRynReplayKendoColumnLike {
  field?: string;
  title?: string;
  hidden?: boolean;
  isColumnGroup?: boolean;
  children?:
    | { toArray?: () => IRynReplayKendoColumnLike[] }
    | IRynReplayKendoColumnLike[];
}

export interface IRynReplayKendoGridLike {
  columns?:
    | { toArray?: () => IRynReplayKendoColumnLike[] }
    | IRynReplayKendoColumnLike[]
    | null;
}

const childrenOf = (
  col: IRynReplayKendoColumnLike
): IRynReplayKendoColumnLike[] => {
  const kids = col.children;
  if (!kids) {
    return [];
  }
  if (Array.isArray(kids)) {
    return kids;
  }
  return typeof kids.toArray === 'function' ? kids.toArray() : [];
};

const columnsOf = (
  grid: IRynReplayKendoGridLike | null | undefined
): IRynReplayKendoColumnLike[] => {
  const cols = grid?.columns;
  if (!cols) {
    return [];
  }
  if (Array.isArray(cols)) {
    return cols;
  }
  return typeof cols.toArray === 'function' ? cols.toArray() : [];
};

/**
 * Visible leaf columns currently on the grid (user column state / defaults).
 * Skips checkbox / internal `__*` fields and hidden columns.
 */
export function extractVisibleKendoGridColumns(
  grid: IRynReplayKendoGridLike | null | undefined
): IRynReplayGridColumnDef[] {
  const out: IRynReplayGridColumnDef[] = [];
  const walk = (cols: IRynReplayKendoColumnLike[]) => {
    for (const col of cols) {
      if (col.isColumnGroup) {
        walk(childrenOf(col));
        continue;
      }
      if (col.hidden) {
        continue;
      }
      const field = typeof col.field === 'string' ? col.field.trim() : '';
      if (!field || field.startsWith('__')) {
        continue;
      }
      out.push({
        key: field,
        label:
          typeof col.title === 'string' && col.title.trim()
            ? col.title.trim()
            : field,
      });
    }
  };
  walk(columnsOf(grid));
  return out;
}

const asCell = (value: unknown): string | number | boolean => {
  if (typeof value === 'boolean' || typeof value === 'number') {
    return Number.isFinite(value as number) || typeof value === 'boolean'
      ? (value as number | boolean)
      : '';
  }
  if (value == null) {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'object') {
    // Nested detail objects are not serialized cell-by-cell — keep a count hint
    // when it looks like an array, otherwise empty (avoid PII dumps).
    return Array.isArray(value) ? value.length : '';
  }
  return String(value);
};

const readPath = (row: unknown, path: string): unknown => {
  if (!row || typeof row !== 'object') {
    return undefined;
  }
  const parts = path.split('.');
  let cur: unknown = row;
  for (const part of parts) {
    if (!cur || typeof cur !== 'object') {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
};

/**
 * Slim one row to the recorded column keys. Optional `getValue` overrides
 * path reads for computed cells (e.g. detailCount).
 */
export function slimRowByColumns(
  row: unknown,
  columns: readonly IRynReplayGridColumnDef[],
  getValue?: (row: unknown, key: string) => unknown
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const col of columns) {
    const raw = getValue ? getValue(row, col.key) : readPath(row, col.key);
    out[col.key] = asCell(raw);
  }
  return out;
}
